import {
  CloseCircleFilled,
  CloseCircleOutlined,
  FilterOutlined,
  InfoCircleOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import {
  Button,
  DatePicker,
  Form,
  Input,
  Modal,
  Pagination,
  Space,
  Table,
  Typography,
  Tooltip,
} from 'antd';
import { type FC } from 'react';

import { rangePickerLocale } from '@app//constants/rangePickerLocale';
import {
  CloseIconsForModalWithDataTestId,
  EmptyComponentWithDataTestId,
  Error403Unauthorized,
  MultipleSelect,
} from '@app/components';
import { CustomAlert } from '@app/components/CustomAlert';
import { DATE_PICKER_FORMAT, OFF, PAYMENT_SYSTEMS } from '@app/constants';
import { ERROR } from '@app/constants/alerts';
import { PREFIX } from '@app/constants/appPrefix';
import {
  COLLECTION_LABEL,
  EXPENSES_LABEL,
  PAYMENTS_LABEL,
} from '@app/constants/clientPaymentsLabels';
import { CLIENT_PAYMENTS_OPERATIONS } from '@app/constants/clientPaymentsOperations';
import { ACQUIRING, PAY_TYPES } from '@app/constants/payTypes';
import { REGISTRY_SEARCH_RULES } from '@app/constants/registrySearchRules';
import { CLIENTS_PAYMENTS_HEADER, U_KEY } from '@app/constants/tables';
import { PaymentInfoModal } from '@app/pages/Clients/ClientPayments/ClientPaymentsList/components';
import { generateSpanWithDataTestId } from '@app/shared/utils';
import { divideAmount } from '@app/utils';

import { AddingModal } from '@pages/Clients/ClientPayments/ClientPaymentsList/components/AddingModal';

import {
  useClientPaymentsList,
  useClientPaymentsListEffects,
} from '@pages/Clients/ClientPayments/ClientPaymentsList/hooks';
import { adaptDataToTable } from '@pages/Clients/ClientPayments/ClientPaymentsList/utils/adaptDataToTable';

import '@pages/Clients/ClientPayments/ClientPaymentsList/styles/ClientPayments.desktop.scss';
import '@pages/Clients/ClientPayments/ClientPaymentsList/styles/ClientPayments.tablet.scss';
import '@pages/Clients/ClientPayments/ClientPaymentsList/styles/ClientPayments.mobile.scss';

const { RangePicker } = DatePicker;
const { Text } = Typography;

export const ClientPaymentsList: FC = () => {
  const {
    isLoading,
    payments,
    form,
    toolbarClass,
    isMobile,
    searchError,
    setSearchError,
    selectedPayTypes,
    setSelectedPayTypes,
    selectedUsers,
    setSelectedUsers,
    selectedPaymentAccounts,
    setSelectedPaymentAccounts,
    selectedLegalEntities,
    setSelectedLegalEntities,
    selectedOperations,
    setSelectedOperations,
    selectedArticles,
    setSelectedArticles,
    hideFilters,
    handlePageChange,
    totalRowsCount,
    totalAmount,
    pageNumber,
    rowsPerPage,
    handleReset,
    submitForm,
    weekBeforeToday,
    today,
    isPaymentsModalOpen,
    isCollectionsModalOpen,
    isExpensesModalOpen,
    setIsPaymentsModalOpen,
    setIsCollectionsModalOpen,
    setIsExpensesModalOpen,
    addPaymentsForm,
    paymentAccounts,
    addCollectionsForm,
    addCExpensesForm,
    submitPayment,
    isModalLoading,
    submitCollection,
    submitExpense,
    selectedPaymentAccountId,
    setSelectedPaymentAccountId,
    rowSelection,
    selectionCount,
    isArchivingModalOpen,
    setIsArchivingModalOpen,
    deletionLoader,
    deleteRows,
    MassActionButton,
    hasUserRightForModuleView,
    hasUserRightToCreateExpense,
    hasUserRightToCreateCollection,
    hasUserRightToToppingUpRegister,
    initialSearch,
    hideToolbar,
    handleFileClick,
    handleFileOpen,
    usersSelectOptions,
    paymentAccountsSelectOptions,
    legalEntitiesSelectOptions,
    expenseArticlesSelectOptions,
    filtersData,
    setIsModalLoading,
    getPaymentAccountsForSystemHandler,
    selectedPaymentSystems,
    setSelectedPaymentSystems,
    hasAccessRightsToPaymentSystemsSelector,
    handlePaymentInfoModalOpen,
    paymentInfoModalOpen,
    setPaymentInfoModalOpen,
    objectId,
    setObjectId,
  } = useClientPaymentsList();

  useClientPaymentsListEffects({
    initialSearch,
    isMobile,
    MassActionButton,
    submitForm,
    hideToolbar,
  });

  if (!hasUserRightForModuleView) {
    return <Error403Unauthorized />;
  }

  return (
    <div className={`${PREFIX}client-payments`}>
      <div className="header-container">
        <Form
          form={form}
          autoComplete={OFF}
          onFinish={submitForm}
          labelCol={{ span: 4 }}
          wrapperCol={{ span: 30 }}
          style={{ marginBottom: 10 }}
          layout="vertical"
          initialValues={{
            Date: [weekBeforeToday, today],
          }}
        >
          <div className={toolbarClass}>
            <div className="date-container">
              <div className="date-item">
                <Text className="label" strong data-testid="period-title">
                  Период:
                </Text>
                <Form.Item name="Date">
                  <RangePicker
                    format={DATE_PICKER_FORMAT}
                    size="middle"
                    allowEmpty={[true, true]}
                    locale={rangePickerLocale}
                    allowClear={{
                      clearIcon: (
                        <CloseCircleFilled data-testid="delete-period" />
                      ),
                    }}
                  />
                </Form.Item>
              </div>
            </div>

            <Form.Item
              name="Search"
              wrapperCol={{ span: 16 }}
              rules={REGISTRY_SEARCH_RULES.search}
            >
              <Input
                placeholder="Введите имя, ID или телефон клиента"
                addonBefore={<SearchOutlined />}
                data-testid="search-input"
              />
            </Form.Item>

            <div className="selectors-container">
              <div className="selectors">
                {isMobile && (
                  <Button
                    type="primary"
                    className="show-filters"
                    onClick={hideFilters}
                  >
                    <FilterOutlined /> Фильтры
                  </Button>
                )}

                <MultipleSelect
                  className="user-selector"
                  placeholder="Все пользователи"
                  allOptions={usersSelectOptions}
                  width={100}
                  selectedOption={selectedUsers}
                  setSelectedOptions={setSelectedUsers}
                  dataTestId="all-users-dropdown"
                  optionDataTestId="name-user-item"
                  filterName="filterName"
                  loader={filtersData === undefined}
                />

                <MultipleSelect
                  className="user-selector"
                  placeholder="Все операции"
                  allOptions={CLIENT_PAYMENTS_OPERATIONS}
                  width={100}
                  selectedOption={selectedOperations}
                  setSelectedOptions={setSelectedOperations}
                  dataTestId="all-operations-dropdown"
                  filterName="filterName"
                  loader={filtersData === undefined}
                />

                <MultipleSelect
                  className="user-selector"
                  placeholder="Все статьи"
                  width={100}
                  allOptions={expenseArticlesSelectOptions}
                  selectedOption={selectedArticles}
                  setSelectedOptions={setSelectedArticles}
                  dataTestId="all-articles-dropdown"
                  optionDataTestId="article-item"
                  filterName="filterName"
                  loader={filtersData === undefined}
                />

                <MultipleSelect
                  className="user-selector"
                  placeholder="Все виды оплат"
                  allOptions={PAY_TYPES}
                  width={100}
                  selectedOption={selectedPayTypes}
                  setSelectedOptions={setSelectedPayTypes}
                  dataTestId="all-payment-types-dropdown"
                  filterName="filterName"
                  loader={filtersData === undefined}
                />

                <MultipleSelect
                  className="user-selector"
                  placeholder="Все кассы"
                  allOptions={paymentAccountsSelectOptions}
                  width={100}
                  selectedOption={selectedPaymentAccounts}
                  setSelectedOptions={setSelectedPaymentAccounts}
                  dataTestId="all-cash-registers-dropdown"
                  optionDataTestId="payment-account-item"
                  filterName="filterName"
                  loader={filtersData === undefined}
                />

                <MultipleSelect
                  className="user-selector"
                  placeholder="Все юр. лица"
                  allOptions={legalEntitiesSelectOptions}
                  width={100}
                  selectedOption={selectedLegalEntities}
                  setSelectedOptions={setSelectedLegalEntities}
                  dataTestId="all-legal-entities-dropdown"
                  optionDataTestId="legal-entity-item"
                  filterName="filterName"
                  loader={filtersData === undefined}
                />

                {hasAccessRightsToPaymentSystemsSelector && (
                  <MultipleSelect
                    className="user-selector"
                    placeholder="Все платёжные системы"
                    allOptions={PAYMENT_SYSTEMS}
                    width={100}
                    selectedOption={selectedPaymentSystems}
                    setSelectedOptions={setSelectedPaymentSystems}
                    dataTestId="payment-systems-dropdown"
                    optionDataTestId="payment-systems-item"
                    filterName="filterName"
                    loader={filtersData === undefined}
                  />
                )}

                <Form.Item
                  style={{
                    display: 'inline-block',
                    marginBottom: 0,
                  }}
                  className="buttons-container"
                >
                  {searchError && (
                    <CustomAlert
                      errorType={ERROR}
                      serverError={searchError}
                      setServerError={setSearchError}
                    />
                  )}
                  <Space className="tablet-buttons" size="middle">
                    <Button
                      htmlType="reset"
                      onClick={handleReset}
                      className="button"
                      data-testid="reset-filters"
                    >
                      <CloseCircleOutlined />
                      Сброс
                    </Button>

                    <Button
                      type="primary"
                      htmlType="submit"
                      className="button"
                      data-testid="search-by-filters"
                    >
                      <FilterOutlined />
                      Поиск
                    </Button>
                  </Space>
                </Form.Item>
              </div>

              <Form.Item
                style={{
                  display: 'inline-block',
                  marginBottom: 0,
                }}
                className="buttons-container"
              >
                {searchError && (
                  <CustomAlert
                    errorType={ERROR}
                    serverError={searchError}
                    setServerError={setSearchError}
                  />
                )}
                <Space className="desktop-buttons" size="middle">
                  <Button
                    htmlType="reset"
                    onClick={handleReset}
                    className="button"
                    data-testid="reset-filters"
                  >
                    <CloseCircleOutlined />
                    Сброс
                  </Button>

                  <Button
                    type="primary"
                    htmlType="submit"
                    className="button"
                    data-testid="search-by-filters"
                  >
                    <FilterOutlined />
                    Поиск
                  </Button>
                </Space>
              </Form.Item>
            </div>
          </div>
        </Form>
      </div>

      <div className="payments-count-container">
        <div className="payments-count-container__info">
          <Text
            className="payments-count"
            type="secondary"
            data-testid="chosen-operations-count"
          >
            {`Выбрано ${selectionCount} из ${divideAmount(
              totalRowsCount
            )} операций`}
          </Text>
          <Text
            className="payments-count-container__total-amount"
            data-testid="total-sum"
          >
            {`Общая сумма: ${totalAmount}`}
          </Text>
          <Tooltip title="Общая сумма всех операций по выбранным фильтрам">
            <InfoCircleOutlined />
          </Tooltip>
        </div>
        {MassActionButton}
      </div>

      <div className={`${PREFIX}client-payments__table`}>
        <Table
          columns={CLIENTS_PAYMENTS_HEADER}
          dataSource={adaptDataToTable({
            payments,
            handleFileClick,
            handleFileOpen,
            handlePaymentInfoModalOpen,
          })}
          rowKey={U_KEY}
          loading={isLoading}
          pagination={false}
          rowSelection={rowSelection}
          rowClassName="operation-item"
          locale={{
            emptyText: <EmptyComponentWithDataTestId />,
          }}
        />
      </div>

      <div className="pagination-container">
        <Pagination
          size={isMobile ? 'small' : 'default'}
          showSizeChanger={true}
          current={pageNumber}
          total={totalRowsCount}
          defaultCurrent={pageNumber}
          pageSize={rowsPerPage}
          onChange={handlePageChange}
          showTotal={(total, range) =>
            `Показаны ${range[0]}-${range[1]} из ${total}`
          }
        />
        {MassActionButton}
      </div>

      <Modal
        title={generateSpanWithDataTestId(
          `Удалить из базы операции (${selectionCount})`,
          'delete-operations-popup-title'
        )}
        open={isArchivingModalOpen}
        cancelText="Отмена"
        okText="Удалить"
        okType="danger"
        onOk={deleteRows}
        okButtonProps={{
          type: 'primary',
          loading: deletionLoader,
          'data-testid': 'confirm-delete-operation',
        }}
        cancelButtonProps={{
          loading: deletionLoader,
          'data-testid': 'cancel-delete-operation',
        }}
        onCancel={() => setIsArchivingModalOpen(false)}
        closeIcon={<CloseIconsForModalWithDataTestId />}
      >
        <p data-testid="delete-operations-description">
          Вы уверены, что хотите удалить из базы операции ({selectionCount})?
        </p>
      </Modal>

      {hasUserRightToToppingUpRegister && (
        <AddingModal
          title={PAYMENTS_LABEL}
          titleDataTestId="cash-replenishment-popup-title"
          isModalOpen={isPaymentsModalOpen}
          setIsModalOpen={setIsPaymentsModalOpen}
          form={addPaymentsForm}
          paymentAccounts={paymentAccounts.filter(
            ({ PayType }) => PayType !== ACQUIRING
          )}
          className="adding-modal"
          submit={submitPayment}
          isModalLoading={isModalLoading}
          selectedPaymentAccountId={selectedPaymentAccountId}
          setSelectedPaymentAccountId={setSelectedPaymentAccountId}
          isExpense={false}
          isCollection={false}
          initialSearch={initialSearch}
          setIsModalLoading={setIsModalLoading}
          getPaymentAccountsForSystemHandler={
            getPaymentAccountsForSystemHandler
          }
        />
      )}

      {hasUserRightToCreateCollection && (
        <AddingModal
          title={COLLECTION_LABEL}
          titleDataTestId="encashment-popup-title"
          isModalOpen={isCollectionsModalOpen}
          setIsModalOpen={setIsCollectionsModalOpen}
          form={addCollectionsForm}
          paymentAccounts={paymentAccounts}
          className="adding-modal"
          submit={submitCollection}
          isModalLoading={isModalLoading}
          selectedPaymentAccountId={selectedPaymentAccountId}
          setSelectedPaymentAccountId={setSelectedPaymentAccountId}
          isExpense={false}
          isCollection
          initialSearch={initialSearch}
          setIsModalLoading={setIsModalLoading}
          getPaymentAccountsForSystemHandler={
            getPaymentAccountsForSystemHandler
          }
        />
      )}

      {hasUserRightToCreateExpense && (
        <AddingModal
          title={EXPENSES_LABEL}
          isModalOpen={isExpensesModalOpen}
          setIsModalOpen={setIsExpensesModalOpen}
          form={addCExpensesForm}
          paymentAccounts={paymentAccounts}
          className="adding-modal"
          submit={submitExpense}
          isModalLoading={isModalLoading}
          selectedPaymentAccountId={selectedPaymentAccountId}
          setSelectedPaymentAccountId={setSelectedPaymentAccountId}
          isExpense
          isCollection={false}
          initialSearch={submitForm}
          titleDataTestId="add-expense-popup-title"
          setIsModalLoading={setIsModalLoading}
          getPaymentAccountsForSystemHandler={
            getPaymentAccountsForSystemHandler
          }
        />
      )}
      <PaymentInfoModal
        open={paymentInfoModalOpen}
        setOpen={setPaymentInfoModalOpen}
        objectId={objectId}
        setObjectId={setObjectId}
      />
    </div>
  );
};
